import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  SxProps,
  Theme,
  Divider,
  CircularProgress,
} from "@mui/material";
import React, { ReactNode } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

export interface ConfirmationPopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: string;
  confirmHoverColor?: string;
  confirmTextHoverColor?: string;
  icon?: ReactNode;
  iconColor?: string;
  iconBackgroundColor?: string;
  loading?: boolean;
  maxWidth?: "xs" | "sm" | "md" | "lg" | "xl" | false;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  disableBackdropClick?: boolean;
  disableEscapeKeyDown?: boolean;
  contentSx?: SxProps<Theme>;
  titleSx?: SxProps<Theme>;
  actionsSx?: SxProps<Theme>;
  paperSx?: SxProps<Theme>;
  disableConfirm?: boolean;
}

const ConfirmationPopup: React.FC<ConfirmationPopupProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  confirmColor = "error",
  confirmHoverColor,
  confirmTextHoverColor,
  icon,
  iconColor,
  iconBackgroundColor,
  loading = false,
  maxWidth = "sm",
  fullWidth = true,
  showCloseButton = true,
  disableBackdropClick = false,
  disableEscapeKeyDown = false,
  contentSx,
  titleSx,
  actionsSx,
  paperSx,
  disableConfirm = false,
}) => {
  const { colors } = useCelleraTheme();

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm();
  };

  const defaultPaperSx: SxProps<Theme> = {
    borderRadius: 3,
    margin: "16px",
    ...paperSx,
  };

  const defaultTitleSx: SxProps<Theme> = {
    pb: 1,
    ...titleSx,
  };

  const defaultContentSx: SxProps<Theme> = {
    p: 0,
    ...contentSx,
  };

  const defaultActionsSx: SxProps<Theme> = {
    gap: 2,
    p: "16px",
    justifyContent: "flex-end",
    display: "flex",
    ...actionsSx,
  };

  return (
    <Dialog
      open={open}
      onClose={disableBackdropClick ? undefined : handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      disableEscapeKeyDown={disableEscapeKeyDown}
      slotProps={{
        paper: {
          sx: defaultPaperSx,
        },
      }}
    >
      <DialogTitle sx={defaultTitleSx}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {icon && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: iconBackgroundColor || colors.warning[500],
                  color: iconColor || colors.text.primary,
                }}
              >
                {icon}
              </Box>
            )}
            <Typography
              variant="h5"
              className="font-semibold"
              sx={{ color: colors.text.primary }}
            >
              {title}
            </Typography>
          </Box>
          {showCloseButton && (
            <IconButton
              onClick={handleClose}
              disabled={loading}
              size="small"
              sx={{
                color: colors.text.secondary,
                "&:hover": {
                  backgroundColor: colors.background.secondary,
                  color: colors.text.primary,
                },
              }}
            >
              ×
            </IconButton>
          )}
        </Box>
      </DialogTitle>

      <Divider />

      <DialogContent sx={defaultContentSx}>
        {typeof message === "string" ? (
          <Typography
            variant="body1"
            sx={{
              color: colors.text.primary,
              fontSize: "18px",
              py: "24px",
              px: "16px",
              lineHeight: 1.5,
            }}
          >
            {message}
          </Typography>
        ) : (
          message
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={defaultActionsSx}>
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            borderColor: colors.border.main,
            borderRadius: "80px",
            width: "150px",
            color: colors.text.primary,
            "&:hover": {
              borderColor: colors.border.dark,
              backgroundColor: colors.background.secondary,
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || disableConfirm}
          sx={{
            textTransform: "none",
            backgroundColor: confirmColor || colors.primary.main,
            borderRadius: "80px",
            width: "150px",
            fontWeight: 500,
            "&:disabled": {
              backgroundColor: colors.border.light,
              color: colors.text.secondary,
            },
            "&:hover": {
              backgroundColor: confirmHoverColor || colors.primary.dark,
              color: confirmTextHoverColor || colors.text.inverse,
            },
          }}
        >
          {loading ? <CircularProgress size={20} /> : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationPopup;
